import { faTrashAlt } from '@awesome.me/kit-a1e2571845/icons/classic/regular'
import { zodResolver } from '@hookform/resolvers/zod'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { createFileRoute, notFound, redirect } from '@tanstack/react-router'
import { Icon, TelInput } from '@xweather/theme'
import i18next from 'i18next'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { z } from 'zod'
import { NavigateWithMessage } from '../../../components/errors/NotFound.tsx'
import { FormLayout2 } from '../../../components/FormLayout2.tsx'
import { I18n } from '../../../components/I18n.tsx'
import { useConfirmDialog } from '../../../components/useConfirmDialog.tsx'
import { currentUserData } from '../../../data/userProfile.ts'
import { type InsightUser, roleData, useDeleteUser, userData, useUpdateUser } from '../../../data/users.ts'
import { canEditRole, getSelectableRoles, RoleDescriptions } from './-roleUtils.tsx'

export const Route = createFileRoute('/_app/users/$id')({
	loader: async ({ context, params }) => {
		const [users, currentUser] = await Promise.all([userData.ensure(context), currentUserData.ensure(), roleData.ensure()])
		const user = users.find((u) => u.id === params.id)
		if (!user) throw notFound()
		if (currentUser.id !== user.id && !canEditRole(currentUser, user.role)) throw redirect({ to: '/users' })
		return user
	},
	notFoundComponent: () => <NavigateWithMessage options={{ to: '/users' }} message={<I18n i18nKey={($) => $.users.notFound} />} />,
	component: EditUser,
})

function EditUser() {
	const user = Route.useLoaderData()
	const navigate = Route.useNavigate()
	const { data: currentUser } = currentUserData.use()
	const { mutateAsync: updateUser, isPending } = useUpdateUser()
	const { mutateAsync: deleteUser } = useDeleteUser()
	const [activeStep, setActiveStep] = useState(0)

	const canEdit = currentUser.id !== user.id && canEditRole(currentUser, user.role)

	const form = useForm<EditUserValues>({
		resolver: zodResolver(editUserSchema),
		defaultValues: { name: user.fullName, role: user.userRoles.at(0) ?? '', blocked: user.isBlocked },
	})

	const { Dialog: deleteDialog, openDialog: openDeleteDialog } = useConfirmDialog({
		title: <I18n i18nKey={($) => $.users.deleteUser} />,
		content: <I18n i18nKey={($) => $.users.deleteUserConfirmation} values={{ user }} />,
		actionName: <I18n i18nKey={($) => $.delete} />,
		color: 'error',
		onConfirm: async () => {
			await deleteUser(user.id, {
				onSuccess: async () => {
					enqueueSnackbar(<I18n i18nKey={($) => $.users.deleteSuccess} />, { variant: 'success' })
					await navigate({ to: '/users' })
				},
				onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.users.deleteFailure} />, { variant: 'error' }),
			})
		},
	})

	const onSubmit = form.handleSubmit((data) =>
		updateUser(
			{ id: user.id, body: { name: data.name, roles: [data.role], blocked: data.blocked, type: 'Individual', subType: 'UIUser' } },
			{
				onSuccess: async () => {
					enqueueSnackbar(<I18n i18nKey={($) => $.users.updateSuccess} />, { variant: 'success' })
					await navigate({ to: '/users' })
				},
				onError: () => enqueueSnackbar(<I18n i18nKey={($) => $.users.updateFailure} />, { variant: 'error' }),
			},
		),
	)

	const steps = [
		{
			id: 'details',
			label: <I18n i18nKey={($) => $.users.formSteps.detailsLabel} />,
			description: <I18n i18nKey={($) => $.users.formSteps.detailsDescription} />,
			form: <DetailsStep user={user} canEdit={canEdit} />,
		},
	]

	return (
		<FormProvider {...form}>
			<Box component='form' sx={{ display: 'contents' }} onSubmit={onSubmit}>
				<FormLayout2
					onBack={() => navigate({ to: '/users' })}
					header={<I18n i18nKey={($) => $.users.edit} />}
					sideBar={<FormLayout2.Steps steps={steps} activeStep={activeStep} />}
					bottomBar={
						<>
							<Button color='error' variant='text' startIcon={<Icon icon={faTrashAlt} />} disabled={!canEdit} onClick={openDeleteDialog}>
								<I18n i18nKey={($) => $.delete} />
							</Button>
							<FormLayout2.Buttons
								onStepChange={setActiveStep}
								activeStep={activeStep}
								stepCount={steps.length}
								onCancel={() => navigate({ to: '/users' })}
								onNext={form.trigger}
							/>
							{activeStep === steps.length - 1 && (
								<Button type='submit' disabled={isPending}>
									<I18n i18nKey={($) => $.save} />
									{isPending && <CircularProgress sx={{ ml: 1 }} />}
								</Button>
							)}
							{deleteDialog}
						</>
					}
					paperProps={{ sx: { maxW: 640 } }}
				>
					{steps[activeStep]?.form}
				</FormLayout2>
			</Box>
		</FormProvider>
	)
}

const DetailsStep = ({ user, canEdit }: { user: InsightUser; canEdit: boolean }) => {
	const { control } = useFormContext<EditUserValues>()
	const { data: roles } = roleData.use()
	const { data: currentUser } = currentUserData.use()
	const selectableRoles = getSelectableRoles(currentUser, roles)
	return (
		<Stack col spacing={2} sx={{ p: 2 }}>
			<Controller
				control={control}
				name='name'
				render={({ field, fieldState }) => (
					<TextField
						{...field}
						label={<I18n i18nKey={($) => $.users.name} />}
						error={!!fieldState.error}
						helperText={fieldState.error?.message}
					/>
				)}
			/>
			<TextField label={<I18n i18nKey={($) => $.users.email} />} value={user.email.email} disabled />
			<TelInput label={<I18n i18nKey={($) => $.users.phone} />} value={user.phone.phone} disabled />
			<Stack col>
				<Controller
					control={control}
					name='role'
					render={({ field, fieldState }) => (
						<TextField
							{...field}
							select
							label={<I18n i18nKey={($) => $.users.role} />}
							error={!!fieldState.error}
							helperText={fieldState.error?.message}
							disabled={!canEdit}
						>
							{selectableRoles.map((role) => (
								<MenuItem key={role.id} value={role.id}>
									<I18n i18nKey={($) => $.users.roles[role.name]} />
								</MenuItem>
							))}
						</TextField>
					)}
				/>
				<RoleDescriptions />
			</Stack>
			<Controller
				control={control}
				name='blocked'
				render={({ field }) => (
					<FormControlLabel
						control={<Checkbox size='large' checked={field.value} disabled={!canEdit} onChange={(e) => field.onChange(e.target.checked)} />}
						label={<I18n i18nKey={($) => $.users.blocked} />}
					/>
				)}
			/>
		</Stack>
	)
}

const editUserSchema = z.object({
	name: z
		.string()
		.min(2, { error: () => i18next.t(($) => $.users.nameTooShort) })
		.max(100, { error: () => i18next.t(($) => $.users.nameTooLong) }),
	role: z.string(),
	blocked: z.boolean(),
})
type EditUserValues = z.infer<typeof editUserSchema>
